import { useEffect, useRef, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Shield, Bug, FileCode, Activity, TrendingUp, Target,
  Clock, AlertTriangle, Loader2,
  BarChart2, Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { sileo } from 'sileo';
import { Header } from '../../widgets/header';
import { MetricsGrid } from '../../widgets/metrics-grid';
import { ChartsSection } from '../../widgets/charts-section';
import { QualityGateBadge } from '../../widgets/quality-gate';
import { Badge, Card, Button } from '../../shared/ui';
import { useDashboardMetrics, useProjects } from '../../shared/api';
import { useAnalysisStore } from '../../shared/lib/store/analysisStore';

export function DashboardPage() {
  const { t } = useTranslation();
  const localProjectData = useAnalysisStore(state => state.projectData);

  // Fetch dashboard metrics and project list from the API
  const { data: metrics, isLoading, isError, refetch } = useDashboardMetrics();
  const { data: apiProjects } = useProjects();
  const errorToastShown = useRef(false);

  // ── Accumulate stats from ALL local project analyses ─────────────────
  const localStats = useMemo(() => {
    let totalIssues = 0;
    let critical = 0;
    let bugs = 0;           // high severity
    let vulnerabilities = 0; // critical severity
    let codeSmells = 0;     // medium severity
    let linesOfCode = 0;
    let totalAnalyses = 0;
    const projectsCount = Object.keys(localProjectData).length;
    const allIssues: any[] = [];

    Object.values(localProjectData).forEach((project: any) => {
      totalAnalyses += project.results?.length ?? 0;

      project.aggregatedIssues?.forEach((issue: any) => {
        totalIssues++;
        allIssues.push(issue);
        const sev = (issue.severity || '').toLowerCase();
        if (sev === 'critical') { vulnerabilities++; critical++; }
        else if (sev === 'high') bugs++;
        else if (sev === 'medium') codeSmells++;
      });

      project.results?.forEach((r: any) => {
        linesOfCode += r.metrics?.lines || r.linesOfCode || 0;
      });
    });

    return {
      totalIssues: totalIssues || 0,
      critical: critical || 0,
      bugs: bugs || 0,
      vulnerabilities: vulnerabilities || 0,
      codeSmells: codeSmells || 0,
      linesOfCode: linesOfCode || 0,
      totalAnalyses: totalAnalyses || 0,
      projectsCount: projectsCount || 0,
      recentIssues: allIssues.slice(0, 10),
    };
  }, [localProjectData]);

  useEffect(() => {
    if (isError && !errorToastShown.current) {
      errorToastShown.current = true;
      sileo.action({
        title: t('toasts.dashboardError'),
        description: t('toasts.dashboardErrorDesc'),
        button: {
          title: t('toasts.retry'),
          onClick: () => {
            errorToastShown.current = false;
            refetch();
          },
        },
      });
    }
  }, [isError, t, refetch]);

  // ── Merge local + backend stats ──────────────────────────────────────
  const totalBugs = (localStats.bugs || 0) + (metrics?.total_issues?.high || 0);
  const totalVulnerabilities = (localStats.vulnerabilities || 0) + (metrics?.total_issues?.critical || 0);
  const totalSmells = (localStats.codeSmells || 0) + (metrics?.total_issues?.medium || 0);
  const totalProjects = Math.max(localStats.projectsCount || 0, metrics?.total_projects || 0);
  const totalAnalyses = (localStats.totalAnalyses || 0) + (metrics?.total_analyses || 0);
  
  const apiTotalIssues = (metrics?.total_issues?.critical || 0) + (metrics?.total_issues?.high || 0)
    + (metrics?.total_issues?.medium || 0) + (metrics?.total_issues?.low || 0);
  
  const totalIssues = (localStats.totalIssues || 0) + apiTotalIssues;
  const totalLoc = localStats.linesOfCode > 0 ? localStats.linesOfCode : (totalIssues * 50);

  const qualityScore = (() => {
    if (totalIssues === 0 && totalAnalyses === 0) {
      const score = metrics?.average_health_score ?? (metrics ? 100 : null);
      return (score !== null && !isNaN(score)) ? score : (metrics ? 100 : null);
    }
    if (totalIssues === 0) return 100;
    
    const weightedAvg = (
      totalVulnerabilities * 5 +
      totalBugs * 2 +
      totalSmells * 0.5
    ) / Math.max(1, totalIssues);
    
    const penalty = Math.min(60, weightedAvg * Math.log10(totalIssues + 1) * 10);
    const result = Math.max(10, Math.round(100 - penalty));
    return isNaN(result) ? 100 : result;
  })();

  const metricsData = [
    {
      icon: <Activity className="h-5 w-5" />,
      label: t('metrics.coverage'),
      value: totalAnalyses || 0,
      unit: ` ${t('common.analysis', { count: totalAnalyses })}`,
      trend: 0,
      status: 'good' as const,
    },
    {
      icon: <Bug className="h-5 w-5" />,
      label: t('metrics.bugs'),
      value: totalBugs || 0,
      trend: 0,
      status: totalBugs > 20 ? 'warning' as const : 'good' as const,
    },
    {
      icon: <AlertTriangle className="h-5 w-5" />,
      label: t('metrics.vulnerabilities'),
      value: totalVulnerabilities || 0,
      trend: 0,
      status: totalVulnerabilities > 0 ? 'warning' as const : 'good' as const,
    },
    {
      icon: <FileCode className="h-5 w-5" />,
      label: t('metrics.codeSmells'),
      value: totalSmells || 0,
      trend: 0,
      status: 'good' as const,
    },
    {
      icon: <Clock className="h-5 w-5" />,
      label: t('metrics.technicalDebt'),
      value: `${totalIssues || 0} issues`,
      status: totalIssues > 50 ? 'warning' as const : 'good' as const,
    },
    {
      icon: <Shield className="h-5 w-5" />,
      label: t('metrics.duplications'),
      value: totalProjects || 0,
      unit: ` ${t('common.project', { count: totalProjects })}`,
      status: 'good' as const,
    },
    {
      icon: <Target className="h-5 w-5" />,
      label: t('metrics.securityHotspots'),
      value: totalVulnerabilities || 0,
      status: totalVulnerabilities > 0 ? 'warning' as const : 'good' as const,
    },
    {
      icon: <TrendingUp className="h-5 w-5" />,
      label: t('metrics.linesOfCode'),
      value: (totalLoc || 0).toLocaleString(),
      status: 'good' as const,
    },
  ];

  const enrichedProjects = useMemo(() => {
    const apiList = apiProjects ?? metrics?.projects?.map((p: any) => ({
      id: p.project_id,
      name: p.project_name,
      last_analysis: p.last_analysis_at,
      health_score: p.health_score,
    })) ?? [];

    const result = apiList.map((p: any) => {
      const localData = localProjectData[p.id];
      const localIssues = localData?.aggregatedIssues?.length ?? 0;
      const localAnalyses = localData?.results?.length ?? 0;
      const localLastUpdated = localData?.lastUpdated;

      const critical = localData?.aggregatedIssues?.filter((i: any) => (i.severity || '').toLowerCase() === 'critical').length ?? 0;
      const high = localData?.aggregatedIssues?.filter((i: any) => (i.severity || '').toLowerCase() === 'high').length ?? 0;
      const penalty = (critical * 15) + (high * 10) + ((localIssues - critical - high) * 3);
      const health = localIssues > 0
        ? Math.max(0, Math.min(100, 100 - (penalty / Math.max(1, localAnalyses))))
        : (p.health_score || null);

      return {
        id: p.id,
        name: p.name,
        lastAnalysis: localLastUpdated ?? p.last_analysis ?? null,
        issues: localIssues,
        analyses: localAnalyses,
        health: isNaN(health) ? 100 : health,
        isActive: p.is_active ?? true,
      };
    });

    Object.entries(localProjectData).forEach(([pid, localData]: [string, any]) => {
      const alreadyIncluded = result.some((p: any) => p.id === pid);
      if (!alreadyIncluded) {
        const localIssues = localData.aggregatedIssues?.length ?? 0;
        const localAnalyses = localData.results?.length ?? 0;
        const critical = localData.aggregatedIssues?.filter((i: any) => (i.severity || '').toLowerCase() === 'critical').length ?? 0;
        const high = localData.aggregatedIssues?.filter((i: any) => (i.severity || '').toLowerCase() === 'high').length ?? 0;
        const penalty = (critical * 15) + (high * 10) + ((localIssues - critical - high) * 3);
        const health = localIssues > 0
          ? Math.max(0, Math.min(100, 100 - (penalty / Math.max(1, localAnalyses))))
          : 100;
        result.push({
          id: pid,
          name: `Project ${pid.slice(0, 8)}`,
          lastAnalysis: localData.lastUpdated,
          issues: localIssues,
          analyses: localAnalyses,
          health: isNaN(health) ? 100 : health,
          isActive: true,
        });
      }
    });

    return result.slice(0, 6);
  }, [apiProjects, metrics, localProjectData]);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-8 py-8 space-y-8">
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl font-bold text-text-primary font-mono lowercase">
                devlint/{t('dashboard.title')}
              </h1>
              <div className="mt-2 flex items-center gap-4 text-sm text-text-secondary font-mono">
                <span>{t('dashboard.version')}: v2.4.1</span>
                <span>•</span>
                <span>{t('dashboard.branch')}: main</span>
                <span>•</span>
                {totalAnalyses > 0 ? (
                  <span className="text-primary font-semibold">
                    {t('dashboard.analysesPerformed', { count: totalAnalyses })}
                  </span>
                ) : (
                  <span>{t('dashboard.lastAnalysis')}: --</span>
                )}
                {totalProjects > 0 && (
                  <>
                    <span>•</span>
                    <span>{t('dashboard.projectsCount', { count: totalProjects })}</span>
                  </>
                )}
              </div>
            </div>
            {qualityScore !== null && !isNaN(qualityScore) && (
              <QualityGateBadge
                status={qualityScore >= 80 ? 'passed' : 'failed'}
                score={qualityScore}
              />
            )}
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-text-secondary font-mono">{t('dashboard.loadingMetrics')}</span>
          </div>
        )}

        {(totalAnalyses > 0) && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 flex flex-col gap-1">
              <div className="flex items-center gap-2 text-primary">
                <Zap className="h-4 w-4" />
                <span className="text-xs font-mono uppercase tracking-wide">{t('dashboard.analyses')}</span>
              </div>
              <div className="text-3xl font-bold font-mono text-text-primary">{totalAnalyses || 0}</div>
              <div className="text-xs text-text-muted font-mono">
                {t('dashboard.inProjects', { count: totalProjects || 0 })}
              </div>
            </div>

            <div className="rounded-xl border border-warning/20 bg-warning/5 p-4 flex flex-col gap-1">
              <div className="flex items-center gap-2 text-warning">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-xs font-mono uppercase tracking-wide">{t('dashboard.issuesFound')}</span>
              </div>
              <div className="text-3xl font-bold font-mono text-text-primary">{totalIssues || 0}</div>
              <div className="text-xs text-text-muted font-mono">
                {(totalVulnerabilities || 0)} {t('issues.critical').toLowerCase()} · {(totalBugs || 0)} {t('metrics.bugs').toLowerCase()}
              </div>
            </div>

            <div className="rounded-xl border border-error/20 bg-error/5 p-4 flex flex-col gap-1">
              <div className="flex items-center gap-2 text-error">
                <Bug className="h-4 w-4" />
                <span className="text-xs font-mono uppercase tracking-wide">{t('metrics.vulnerabilities')}</span>
              </div>
              <div className="text-3xl font-bold font-mono text-text-primary">{totalVulnerabilities || 0}</div>
              <div className="text-xs text-text-muted font-mono">{t('dashboard.criticalSecurity')}</div>
            </div>

            <div className="rounded-xl border border-success/20 bg-success/5 p-4 flex flex-col gap-1">
              <div className="flex items-center gap-2 text-success">
                <BarChart2 className="h-4 w-4" />
                <span className="text-xs font-mono uppercase tracking-wide">{t('dashboard.linesAnalyzed')}</span>
              </div>
              <div className="text-3xl font-bold font-mono text-text-primary">{(totalLoc || 0).toLocaleString()}</div>
              <div className="text-xs text-text-muted font-mono">{t('metrics.linesOfCode').toLowerCase()}</div>
            </div>
          </div>
        )}

        <section>
          <h2 className="text-2xl font-semibold text-text-primary font-mono mb-6">
            {t('dashboard.keyMetrics')}
          </h2>
          <MetricsGrid metrics={metricsData} />
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-text-primary font-mono mb-6">
            {t('dashboard.trends')}
          </h2>
          <ChartsSection metrics={metrics} localStats={localStats} />
        </section>

        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-text-primary font-mono">
              {t('dashboard.recentProjects') || 'Proyectos Recientes'}
            </h2>
            <Link to="/projects">
              <Button variant="ghost">
                {t('dashboard.viewAllProjects') || 'Ver Todos'}
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {enrichedProjects.length > 0 ? (
              enrichedProjects.map((project: any) => (
                <Link key={project.id} to="/projects/$projectId" params={{ projectId: project.id }}>
                  <Card padding="md" variant="hover" className="h-full border-primary/20 hover:border-primary/50 group">
                    <div className="flex flex-col h-full space-y-3">
                      <div className="flex items-center justify-between">
                        <FileCode className="h-5 w-5 text-primary" />
                        <span className="text-[10px] font-mono text-text-muted">
                          {project.lastAnalysis
                            ? new Date(project.lastAnalysis).toLocaleDateString()
                            : 'Sin Análisis'}
                        </span>
                      </div>

                      <h3 className="text-lg font-bold text-text-primary font-mono group-hover:text-primary transition-colors truncate">
                        {project.name}
                      </h3>

                      <div className="flex items-center gap-3 text-xs font-mono text-text-muted">
                        <span>{t('dashboard.analysesPerformed', { count: project.analyses || 0 })}</span>
                        {project.issues > 0 && (
                          <>
                            <span>·</span>
                            <span className="text-warning">{project.issues || 0} issues</span>
                          </>
                        )}
                      </div>

                      {project.health !== null && !isNaN(project.health) && (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-[10px] font-mono text-text-muted">
                            <span>{t('dashboard.healthScore')}</span>
                            <span className={project.health >= 70 ? 'text-success' : project.health >= 40 ? 'text-warning' : 'text-error'}>
                              {(project.health || 0).toFixed(0)}%
                            </span>
                          </div>
                          <div className="w-full h-1 bg-surface-active rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${project.health >= 70 ? 'bg-success' :
                                project.health >= 40 ? 'bg-warning' : 'bg-error'
                                }`}
                              style={{ width: `${project.health}%` }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-2 mt-auto">
                        <Badge severity="info" size="sm" corners="asymmetric">
                          {project.isActive ? (t('dashboard.active') || 'Activo') : 'Archivado'}
                        </Badge>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))
            ) : (
              <div className="md:col-span-3 border border-dashed border-border rounded-xl p-12 text-center bg-surface/30">
                <p className="text-text-muted font-mono">
                  {t('dashboard.noProjects')}
                </p>
                <Link to="/projects">
                  <Button variant="primary" className="mt-4">{t('dashboard.analyzeFirst')}</Button>
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
